import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Skeleton from "@mui/material/Skeleton";

interface TableCardSkeletonProps {
  /** Number of field rows to show */
  fieldCount?: number;
  /** Whether to show action skeleton at bottom */
  showActions?: boolean;
  /** Whether to show status skeleton */
  showStatus?: boolean;
}

/**
 * Skeleton loading state for MobileCard.
 * Matches the layout of MobileCard for consistent visual loading experience.
 *
 * @example
 * // Basic usage
 * <TableCardSkeleton />
 *
 * // With custom field count
 * <TableCardSkeleton fieldCount={4} showActions />
 */
export function TableCardSkeleton({
  fieldCount = 3,
  showActions = false,
  showStatus = false,
}: TableCardSkeletonProps) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Header: Title + Status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
          {showStatus && (
            <Box sx={{ ml: 1, flexShrink: 0 }}>
              <Skeleton variant="rounded" width={64} height={24} />
            </Box>
          )}
        </Box>

        {/* Fields */}
        <Box sx={{ mt: 1.5 }}>
          {Array.from({ length: fieldCount }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.5,
              }}
            >
              <Skeleton variant="text" width="30%" height={20} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          ))}
        </Box>

        {/* Actions */}
        {showActions && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
              mt: 2,
              pt: 1.5,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Skeleton variant="rounded" width={72} height={36} />
            <Skeleton variant="rounded" width={72} height={36} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
